import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import App from "./App.vue";
import router from "./router";
import VCalendar from "v-calendar";
import "v-calendar/style.css";
import VueViewer from 'v-viewer'
import "viewerjs/dist/viewer.css";

// Import translations
import en from "./locales/en.json";
import sk from "./locales/sk.json";
import de from "./locales/de.json";
import pl from "./locales/pl.json";
import hu from "./locales/hu.json";

import { logMessage } from "./utils/flaskapi";

// Flag to track if user country has been detected
let userCountryDetected = false;
let userCountryPromise = null;

async function getUserCountry() {
  // If already detected, return the cached promise
  if (userCountryDetected && userCountryPromise) {
    return userCountryPromise;
  }

  // If detection is in progress, return the existing promise
  if (userCountryPromise && !userCountryDetected) {
    return userCountryPromise;
  }

  // Create a new promise for country detection
  userCountryPromise = (async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      try {
        logMessage(
          "info",
          `User_${data.country_name}_${data.region}_${data.city}_${data.ip}`
        );
        logMessage("info", `User_br_lang:${navigator.language}`);
      } catch (error) {
        logMessage("error", `Error_logging_user_data:${error}`);
      }

      userCountryDetected = true;
      return data.country_code; // e.g., "SK"
    } catch (error) {
      logMessage("error", `Error_detecting_country:${error}`);
      userCountryDetected = true;
      return "EN"; // Default to English on error
    }
  })();

  return userCountryPromise;
}

function getBrowserLanguage() {
  // Get browser language
  const browserLang = navigator.language || navigator.userLanguage;
  const lang = browserLang.split("-")[0].toLowerCase();
  // Map browser language to supported languages
  const languageMap = {
    sk: "sk", // Slovak
    cs: "sk", // Czech maps to Slovak
    de: "de", // German
    pl: "pl", // Polish
    hu: "hu", // Hungarian
    en: "en", // English
    at: "de", // Austria maps to German
  };

  return languageMap[lang] || "sk";
}

export async function detectLocale() {
  try {
    // First, try to get language from localStorage
    const savedLang = localStorage.getItem("preferredLanguage");
    if (savedLang && ["en", "sk", "de", "pl", "hu"].includes(savedLang)) {
      return savedLang;
    }

    // If no saved language, use browser language
    const detectedLang = getBrowserLanguage();

    // Save the detected language
    localStorage.setItem("preferredLanguage", detectedLang);

    return detectedLang;
  } catch (error) {
    logMessage("error", `Error_detecting_locale:${error}`);
    return "sk"; // Default to Slovak
  }
}

// Create i18n instance
const i18n = createI18n({
  legacy: false, // Use Composition API
  locale: 'sk',
  fallbackLocale: "sk",
  messages: {
    en,
    sk,
    de,
    pl,
    hu,
  },
  cookieKey: "lang",
});

// Initialize app with async setup
async function initializeApp() {
  const detectedLocale = await detectLocale();
  i18n.global.locale.value = detectedLocale;

  const app = createApp(App);
  app.use(router);
  app.use(i18n);
  app.use(VCalendar);
  app.use(VueViewer, {defaultOptions: {
    toolbar: false,
    title: false,  
  }});
  app.mount("#app");
  i18n.global.locale.value = detectedLocale;

  // Call getUserCountry only once after app is mounted
  await getUserCountry();
}

// Start the application
initializeApp();
